/**
 * Turn what somebody typed into a date, or nothing, or a refusal.
 *
 * Three outcomes rather than two, because they mean different things:
 *
 *   null        blank -- the API defaults to now, so send nothing
 *   "invalid"   typed something that is not a date; say so
 *   Date        a real day
 *
 * A `<input type="date">` would avoid the parsing, but React Native has no
 * such control and the app is one codebase. A plain field with an explicit
 * format is honest about that; guessing at "last tuesday" is not.
 */
export function parsePlayedOn(input: string): Date | null | "invalid" {
  const text = input.trim();
  if (text === "") return null;

  // Deliberately strict. Accepting "3/8/26" would mean guessing whether the
  // day or the month comes first, and being wrong about it silently.
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (!match) return "invalid";

  const [, y, m, d] = match;
  const year = Number(y);
  const month = Number(m);
  const day = Number(d);

  // Midday, not midnight: a date stored at 00:00 in one timezone is the
  // previous day in another, and "which day did we play" is the whole point.
  const date = new Date(Date.UTC(year, month - 1, day, 12));

  // Rejects 2026-02-31, which Date would otherwise roll into March.
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return "invalid";
  }

  // A play in the future is a typo, not a plan.
  if (date.getTime() > Date.now() + 86_400_000) return "invalid";

  return date;
}
